/**
 * Shared vocabulary for the usage module.
 *
 * Formatters and small primitives live here so every tab renders the same
 * number the same way. That matters more than it sounds: the page shows costs
 * spanning six orders of magnitude — a $0.0004 Whisper call next to a $423
 * month — and a formatter that rounds to two places turns most of the table
 * into "$0.00" while one that never rounds turns the totals into noise.
 */
import React from 'react';
import { DurationSource, UsageDimension, UsageModality } from '../../lib/api';

// ---------------------------------------------------------------------------
// Palette — one colour per concept, used identically in charts and tables
// ---------------------------------------------------------------------------

export const MODALITY_COLOR: Record<UsageModality, string> = {
  llm: '#3b82f6',        // blue-500
  tts: '#ec4899',        // pink-500
  stt: '#14b8a6',        // teal-500
  telephony: '#f59e0b',  // amber-500 — carrier minutes, not an AI call
  messaging: '#10b981',  // emerald-500 — WhatsApp/SMS sends, matching the channel
};

export const MODALITY_LABEL: Record<UsageModality, string> = {
  llm: 'LLM',
  tts: 'Speech out',
  stt: 'Speech in',
  telephony: 'Carrier',
  messaging: 'Messages',
};

export const CHANNEL_COLOR: Record<string, string> = {
  ui: '#6366f1',        // indigo-500
  phone: '#f59e0b',     // amber-500
  messaging: '#10b981', // emerald-500
  api: '#8b5cf6',
  system: '#94a3b8',
};

export const CHANNEL_LABEL: Record<string, string> = {
  ui: 'Screen',
  phone: 'Phone',
  messaging: 'Messaging',
  api: 'API',
  system: 'System',
  // Spend from before mig 0585, or from a path that never stamped a channel.
  // It is NOT 'Screen' — saying so turned phone calls into web sessions on
  // this very page.
  unknown: 'Unknown',
};

/** Which wire a messaging session actually left on (0588). */
export const TRANSPORT_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  rcs: 'RCS',
  web_messenger: 'Web chat',
};

/** Breakdown dimensions as the page names them — the API keys are not labels. */
export const DIMENSION_LABEL: Record<UsageDimension, string> = {
  org: 'Organization',
  agent: 'Agent',
  agent_kind: 'Agent type',
  channel: 'Channel',
  modality: 'Cost type',
  provider: 'Provider',
  model: 'Model',
  feature: 'Feature',
  campaign: 'Campaign',
  customer: 'Customer',
  status: 'Status',
  transport: 'Transport',
};

/** Where a rate card applies. */
export const SCOPE_LABEL: Record<string, string> = {
  global: 'Global',
  organization: 'Organization',
  agent: 'Agent',
};

/** First letter up, for provider and direction codes that have no label of their own. */
export const capitalise = (s: string | null | undefined): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';

export const TRANSPORT_CHIP: Record<string, string> = {
  whatsapp: 'bg-green-50 text-green-700 border-green-200',
  sms: 'bg-sky-50 text-sky-700 border-sky-200',
  rcs: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  web_messenger: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const CHANNEL_CHIP: Record<string, string> = {
  ui: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  phone: 'bg-amber-50 text-amber-700 border-amber-200',
  messaging: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  api: 'bg-violet-50 text-violet-700 border-violet-200',
  system: 'bg-slate-50 text-slate-600 border-slate-200',
  // Deliberately drab and dashed — an unknown channel is a data-quality gap,
  // and it should look like one rather than like another healthy category.
  unknown: 'bg-white text-gray-500 border-gray-300 border-dashed',
};

export const MODALITY_CHIP: Record<string, string> = {
  llm: 'bg-blue-50 text-blue-700 border-blue-200',
  tts: 'bg-pink-50 text-pink-700 border-pink-200',
  stt: 'bg-teal-50 text-teal-700 border-teal-200',
  telephony: 'bg-amber-50 text-amber-700 border-amber-200',
  messaging: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/**
 * Money, with precision chosen by magnitude.
 *
 * A fixed 2 decimals renders most of this page as "$0.00"; a fixed 6 renders
 * the totals as noise. The scale below keeps roughly three significant digits
 * at every size, which is what makes a per-minute rate and a monthly total
 * legible in the same column.
 */
export const usd = (n: number | null | undefined, dp?: number): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const a = Math.abs(n);
  const digits = dp ?? (a >= 1000 ? 0 : a >= 100 ? 2 : a >= 1 ? 3 : a >= 0.01 ? 4 : 6);
  return `$${n.toFixed(digits)}`;
};

/**
 * Rupees and paise — never a third decimal, because there is no such coin.
 * A figure below half a paisa is shown as "<₹0.01" rather than rounded to a
 * zero that reads as free.
 */
export const inr = (n: number | null | undefined, dp?: number): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const a = Math.abs(n);
  if (a > 0 && a < 0.005) return `${n < 0 ? '-' : ''}<₹0.01`;
  const digits = Math.min(2, dp ?? (a >= 1000 ? 0 : 2));
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

// ---------------------------------------------------------------------------
// Currency — one toggle, every figure
// ---------------------------------------------------------------------------

export type Ccy = 'USD' | 'INR';

/**
 * The page's display currency. Server figures come in USD with an INR twin
 * converted at the fixing of each day's spend (mig 0593); the toggle only
 * chooses which of the two leads. A figure with no INR twin (a unit rate, a
 * projection) is converted at the latest fixing and marked ≈.
 */
export const CurrencyContext = React.createContext<{ ccy: Ccy; rate: number | null; asOf: string | null }>({
  ccy: 'USD', rate: null, asOf: null,
});

const inrDp = (dp?: number) => (dp == null ? undefined : Math.max(0, dp - 2));

export const useCurrency = () => {
  const { ccy, rate: fx, asOf } = React.useContext(CurrencyContext);
  const symbol = ccy === 'INR' ? '₹' : '$';
  /** The selected currency's figure: exact twin when we have one, ≈ at the latest fixing when not. */
  const fmt = React.useCallback((usdValue: number | null | undefined, inrValue?: number | null, dp?: number): string => {
    if (ccy === 'USD') return usd(usdValue, dp);
    if (usdValue == null || Number.isNaN(usdValue)) return '—';
    if (inrValue != null) return inr(inrValue, inrDp(dp));
    if (fx) return `≈${inr(usdValue * fx, inrDp(dp))}`;
    return usd(usdValue, dp);
  }, [ccy, fx]);
  /** The OTHER currency, for the small line under a figure. */
  const alt = React.useCallback((usdValue: number | null | undefined, inrValue?: number | null, dp?: number): string | null => {
    if (usdValue == null || Number.isNaN(usdValue)) return null;
    if (ccy === 'USD') return inrValue != null ? inr(inrValue, inrDp(dp)) : fx ? `≈${inr(usdValue * fx, inrDp(dp))}` : null;
    return usd(usdValue, dp);
  }, [ccy, fx]);
  /** Convert a USD amount for arithmetic display (calculator), at the latest fixing. */
  const toDisplay = React.useCallback((usdValue: number): number => (ccy === 'INR' && fx ? usdValue * fx : usdValue), [ccy, fx]);
  /** A USD unit rate ($/1M chars, $/min) in the selected currency; rates have no INR twin, so ≈ at the latest fixing. */
  const unitRate = React.useCallback((usdValue: number | null | undefined, suffix: string): string => {
    if (usdValue == null || Number.isNaN(usdValue)) return '—';
    if (ccy === 'INR' && fx) return `≈₹${(usdValue * fx).toLocaleString('en-IN', { maximumFractionDigits: 4 })}${suffix}`;
    return rate(usdValue, suffix);
  }, [ccy, fx]);
  /** Axis ticks for a USD series, in the selected currency. */
  const axis = React.useCallback((usdValue: number): string => axisMoney(toDisplay(usdValue), symbol), [toDisplay, symbol]);
  return { ccy, rate: fx, asOf, fmt, alt, toDisplay, unitRate, axis, symbol };
};

/**
 * A money figure in the selected currency, with the other one small beneath.
 * The rupee twin is converted server-side at the fixing of the day the spend
 * happened, so it is not this number times today's rate.
 */
export const Money: React.FC<{
  value: number | null | undefined;
  inr?: number | null;
  dp?: number;
  /** Suppress the second line (dense tables). */
  bare?: boolean;
}> = ({ value, inr: inrValue, dp, bare }) => {
  const { fmt, alt } = useCurrency();
  const second = bare ? null : alt(value, inrValue, dp);
  return (
    <span className="inline-flex flex-col items-end leading-tight">
      <span>{fmt(value, inrValue, dp)}</span>
      {second && <span className="text-[10px] font-normal tabular-nums text-gray-400">{second}</span>}
    </span>
  );
};

export const CurrencyToggle: React.FC<{ onChange: (c: Ccy) => void }> = ({ onChange }) => {
  const { ccy, rate, asOf } = useCurrency();
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-md border border-gray-300 text-xs">
        {(['USD', 'INR'] as Ccy[]).map((c) => (
          <button
            key={c}
            onClick={() => onChange(c)}
            className={`px-2.5 py-1 font-medium ${ccy === c ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            title={c === 'INR' && rate ? `ECB mid-market ₹${rate.toFixed(2)}/$ as of ${asOf}` : undefined}
          >
            {c === 'USD' ? '$ USD' : '₹ INR'}
          </button>
        ))}
      </div>
      {rate && <span className="text-[10px] text-gray-400" title="Each day's spend uses that day's fixing; this is the latest.">₹{rate.toFixed(2)}/$ · {asOf}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CSV — every table leaves the page as the numbers it showed
// ---------------------------------------------------------------------------

export type CsvColumn<T> = { key: string; label: string; get?: (row: T) => unknown };

const csvCell = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'number' ? String(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function downloadCsv<T extends Record<string, unknown>>(filename: string, columns: CsvColumn<T>[], rows: T[]): void {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(c.get ? c.get(r) : r[c.key])).join(','));
  const blob = new Blob([[head, ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const ExportCsv: React.FC<{ onClick: () => void; title?: string; disabled?: boolean }> = ({ onClick, title, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    title={title ?? 'Export this table as CSV — the numbers as shown'}
  >
    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
    CSV
  </button>
);

/**
 * A unit rate: enough digits to be non-zero, and no trailing zeros.
 *
 * Fixed precision is wrong in both directions here — a rate card holds
 * $50/1M chars next to $0.00185/audio-minute, and padding the first to five
 * places ("$50.00000") is as unreadable as truncating the second to two
 * ("$0.00"). Significant digits, trailing zeros dropped.
 */
export const rate = (n: number | null | undefined, suffix: string, symbol = '$'): string =>
  n === null || n === undefined
    ? '—'
    : `${symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 6 })}${suffix}`;

/** The symbol for a currency code a row carries itself (a carrier rate, a call leg). */
export const symbolFor = (currency: string | null | undefined): string =>
  currency === 'INR' ? '₹' : currency === 'USD' || !currency ? '$' : `${currency} `;

export const int = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : Math.round(n).toLocaleString();

export const compact = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(Math.round(n));
};

export const pct = (n: number | null | undefined, dp = 1): string =>
  n === null || n === undefined ? '—' : `${n.toFixed(dp)}%`;

/** Seconds as a duration a human reads at a glance. */
export const duration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

export const minutes = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—'
    : n >= 100 ? `${Math.round(n).toLocaleString()} min`
    : `${n.toFixed(1)} min`;

/**
 * Axis ticks. NOT `usd` — that picks precision from magnitude, which on an
 * axis makes 1.5 and 2 both render as "$2" and leaves a scale with duplicate
 * labels. Ticks want the fewest digits that still distinguish them.
 */
export const axisMoney = (v: number, symbol = '$'): string =>
  `${symbol}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

export const shortDate = (iso: string): string => iso.slice(5).replace('-', '/');

export const dateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${isoDay(d)} ${d.toISOString().slice(11, 16)}`;
};

/**
 * How a duration was measured, said plainly.
 *
 * The distinction is load-bearing rather than cosmetic: only a carrier clock
 * is something we are invoiced for, and only a measured clock may be billed
 * per minute at all. A page that renders all three the same way invites a
 * comparison that isn't valid.
 */
export const DURATION_SOURCE_LABEL: Record<string, string> = {
  carrier: 'Carrier metered',
  engaged: 'Engaged time',
};

export const DURATION_SOURCE_HINT: Record<string, string> = {
  carrier: 'From the call record — the minutes the carrier invoices us for.',
  engaged: 'Sum of per-turn wall clocks. Time in the conversation, not time with it open.',
};

export const DurationBadge: React.FC<{ source: DurationSource }> = ({ source }) => {
  if (!source) {
    return (
      <span
        className="inline-flex items-center rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
        title="No meter for this session — it is excluded from per-minute figures and billed per session only."
      >
        unmetered
      </span>
    );
  }
  const cls = source === 'carrier'
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-indigo-200 bg-indigo-50 text-indigo-700';
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${cls}`}
      title={DURATION_SOURCE_HINT[source]}
    >
      {DURATION_SOURCE_LABEL[source]}
    </span>
  );
};

export const Chip: React.FC<{ kind: 'channel' | 'modality' | 'transport'; value: string }> = ({ kind, value }) => {
  const map = kind === 'channel' ? CHANNEL_CHIP
            : kind === 'transport' ? TRANSPORT_CHIP
            : MODALITY_CHIP;
  const label = kind === 'channel' ? (CHANNEL_LABEL[value] ?? value)
            : kind === 'transport' ? (TRANSPORT_LABEL[value] ?? value)
            : (MODALITY_LABEL[value as UsageModality] ?? value);
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${map[value] ?? 'border-gray-200 bg-gray-50 text-gray-600'}`}>
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

export const Card: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}> = ({ title, subtitle, right, children, className = '', bodyClassName = '' }) => (
  <section className={`rounded-lg bg-white shadow ${className}`}>
    {(title || right) && (
      <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {right && <div className="shrink-0 text-xs text-gray-500">{right}</div>}
      </header>
    )}
    <div className={bodyClassName || 'p-4'}>{children}</div>
  </section>
);

/**
 * A headline number with its unit, its context, and — when there is one — its
 * movement against the previous equal-length window.
 */
export const Kpi: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  deltaPct?: number | null;
  /** For cost, up is bad. For revenue, up is good. */
  deltaIsGood?: (d: number) => boolean;
  accent?: string;
  hint?: string;
}> = ({ label, value, sub, deltaPct, deltaIsGood, accent, hint }) => {
  const good = deltaPct != null && deltaIsGood ? deltaIsGood(deltaPct) : undefined;
  return (
    <div className="rounded-lg bg-white p-4 shadow" title={hint}>
      <div className="flex items-center gap-2">
        {accent && <span className="inline-block h-2 w-2 rounded-full" style={{ background: accent }} />}
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-gray-900">{value}</span>
        {deltaPct != null && (
          <span
            className={`text-xs font-medium tabular-nums ${
              good === undefined ? 'text-gray-500' : good ? 'text-emerald-600' : 'text-rose-600'
            }`}
            title="vs the equally long window immediately before this one"
          >
            {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%
          </span>
        )}
      </div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
};

/** A horizontal share bar — the one chart idiom used across every table. */
export const ShareBar: React.FC<{ pct: number; color?: string }> = ({ pct, color = '#3b82f6' }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
    <div
      className="h-full rounded-full transition-all"
      style={{ width: `${Math.min(100, Math.max(pct, 0.5))}%`, background: color }}
    />
  </div>
);

export const Empty: React.FC<{ children?: React.ReactNode; colSpan?: number }> = ({
  children = 'Nothing in this window.', colSpan,
}) => {
  const cell = <div className="py-8 text-center text-sm text-gray-400">{children}</div>;
  return colSpan ? <tr><td colSpan={colSpan}>{cell}</td></tr> : cell;
};

export const Th: React.FC<{
  /** Optional: a spacer header cell (chevron column, action column) has none. */
  children?: React.ReactNode;
  align?: 'left' | 'right'; className?: string; title?: string;
}> = ({ children, align = 'left', className = '', title }) => (
  <th
    title={title}
    className={`px-3 py-2 text-${align} text-[11px] font-medium uppercase tracking-wide text-gray-500 ${className}`}
  >
    {children}
  </th>
);

export const Td: React.FC<{
  children?: React.ReactNode;
  align?: 'left' | 'right'; className?: string; title?: string; colSpan?: number;
}> = ({ children, align = 'left', className = '', title, colSpan }) => (
  <td colSpan={colSpan} title={title} className={`px-3 py-2 text-${align} text-sm text-gray-700 ${className}`}>
    {children}
  </td>
);

export const Table: React.FC<{ head: React.ReactNode; children: React.ReactNode }> = ({ head, children }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">{head}</thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

/**
 * A banner for something the operator should act on.
 *
 * `tone="warn"` is for data-quality gaps — an unpriced model, an uncosted
 * call. They are not errors; they are places the page is quietly reporting
 * less than the truth, which is the failure mode a cost dashboard hides best.
 */
export const Notice: React.FC<{
  tone?: 'warn' | 'error' | 'info'; children: React.ReactNode; icon?: React.ReactNode;
}> = ({ tone = 'info', children, icon }) => {
  const cls = {
    warn: 'border-amber-200 bg-amber-50 text-amber-900',
    error: 'border-rose-200 bg-rose-50 text-rose-800',
    info: 'border-blue-200 bg-blue-50 text-blue-900',
  }[tone];
  return (
    <div className={`flex items-start gap-2 rounded border px-3 py-2 text-xs ${cls}`}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  );
};
